import { useLayoutEffect, useMemo, useRef, useState } from "preact/hooks";
import { Check, Route, X } from "lucide-react";
import type { MapDocument, MapRouting } from "../shared/types";
import { isMapLocationHidden } from "../shared/mapPresentation";
import { createVenueMapRoutingClient } from "../map/venueMapRoutingClient";
import { createInlineRoutingWorker } from "../map/inlineRoutingWorker";
import type { RoutingFailure } from "../map/venueMapRoutingWorkerProtocol";
import routingWorkerSource from "virtual:event-map-routing-worker-source";
import type { RoutingPoint } from "./routingEditorGeometry";
import "./routing-route-preview.css";

export interface RoutingPreviewResult {
  /** Points and path use source drawing units, suitable for the editor SVG. */
  points: RoutingPoint[];
  path: string;
  from: string;
  to: string;
}

export interface RoutingRoutePreviewProps {
  document: MapDocument;
  routing: MapRouting;
  onRoute: (result: RoutingPreviewResult | null) => void;
  /** Disable while drawing or while another shape is invalid. */
  disabled?: boolean;
}

type PreviewStatus = "idle" | "pending" | "ready" | "blocked" | "failed";
const failureMessage = (failure: RoutingFailure | undefined) =>
  failure === "routing_worker_unavailable"
    ? "Route testing is unavailable in this browser. Try reopening the editor."
    : failure === "routing_timeout"
      ? "This route took too long to calculate. Try a shorter route or simplify the shapes."
      : "The route could not be calculated. Check the shapes and try again.";

/** Checks the current unsaved routing in the same background planner as embeds. */
export function RoutingRoutePreview({
  document,
  routing,
  onRoute,
  disabled = false,
}: RoutingRoutePreviewProps) {
  const [expanded, setExpanded] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [status, setStatus] = useState<PreviewStatus>("idle");
  const [message, setMessage] = useState("");
  const sequence = useRef(0);
  const hasTested = useRef(false);
  const emit = useRef(onRoute);
  emit.current = onRoute;
  const client = useMemo(
    () =>
      createVenueMapRoutingClient(() =>
        createInlineRoutingWorker(routingWorkerSource),
      ),
    [],
  );
  const locations = useMemo(
    () =>
      document.locations
        .filter((location) => !isMapLocationHidden(document, location.id))
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { numeric: true }),
        ),
    [document],
  );
  const snapshot = useMemo<MapDocument>(
    () => ({
      ...document,
      geometry: { ...document.geometry, routing },
    }),
    [document, routing],
  );
  const validPair =
    from !== to &&
    locations.some((location) => location.id === from) &&
    locations.some((location) => location.id === to);

  // Never leave a line on the canvas that was calculated for older edits.
  useLayoutEffect(() => {
    sequence.current++;
    client.cancel();
    emit.current(null);
    setStatus("idle");
    setMessage(
      hasTested.current ? "Test again to check the current layout." : "",
    );
  }, [client, snapshot, from, to, disabled, expanded]);
  useLayoutEffect(
    () => () => {
      sequence.current++;
      client.dispose();
      emit.current(null);
    },
    [client],
  );

  const clear = () => {
    sequence.current++;
    client.cancel();
    emit.current(null);
    hasTested.current = false;
    setStatus("idle");
    setMessage("");
  };
  const testRoute = async () => {
    if (disabled || !validPair || !expanded || status === "pending") return;
    const request = ++sequence.current;
    hasTested.current = true;
    emit.current(null);
    setStatus("pending");
    setMessage("Checking the route…");
    const result = await client.request(snapshot, from, to);
    if (request !== sequence.current || result.status === "cancelled") return;
    if (result.status === "ready") {
      const { width, height } = snapshot.geometry;
      const sourcePoint = (point: { x: number; y: number }): RoutingPoint => [
        point.x * width,
        point.y * height,
      ];
      const points = result.points.map(sourcePoint);
      const path = result.segments.length
        ? [
            `M${sourcePoint(result.segments[0].start).join(",")}`,
            ...result.segments.map((segment) =>
              segment.control
                ? `Q${sourcePoint(segment.control).join(",")} ${sourcePoint(segment.end).join(",")}`
                : `L${sourcePoint(segment.end).join(",")}`,
            ),
          ].join(" ")
        : "";
      emit.current({ points, path, from, to });
      setStatus("ready");
      setMessage("Route found. Follow the line to check walls and openings.");
    } else if (result.failure) {
      setStatus("failed");
      setMessage(failureMessage(result.failure));
    } else {
      setStatus("blocked");
      setMessage(
        "No route found. Check for closed doorways, gaps between walls, or locations outside a walkable area.",
      );
    }
  };

  return (
    <details
      className="em-routing-route-test"
      onToggle={(event) => setExpanded(event.currentTarget.open)}
    >
      <summary>
        <Route size={15} aria-hidden="true" />
        Test a route
      </summary>
      <div className="em-routing-route-test-content">
        <p>Check a journey against these edits before applying them.</p>
        {locations.length < 2 ? (
          <p>Add at least two visible locations to the map to test a route.</p>
        ) : (
          <>
            <div className="em-routing-route-endpoints">
              <label>
                From
                <select
                  aria-label="Route start"
                  value={from}
                  onChange={(event) => setFrom(event.currentTarget.value)}
                >
                  <option value="">Choose a location</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                To
                <select
                  aria-label="Route destination"
                  value={to}
                  onChange={(event) => setTo(event.currentTarget.value)}
                >
                  <option value="">Choose a location</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="em-routing-route-test-actions">
              <button
                type="button"
                className="em-button em-button-secondary"
                disabled={disabled || !validPair || status === "pending"}
                onClick={() => void testRoute()}
              >
                <Route size={14} aria-hidden="true" />
                {status === "pending" ? "Checking…" : "Test route"}
              </button>
              {status !== "idle" && (
                <button
                  type="button"
                  className="em-button em-button-secondary"
                  onClick={clear}
                >
                  <X size={14} aria-hidden="true" />
                  Clear test route
                </button>
              )}
            </div>
            {disabled ? (
              <p>Finish drawing and correct any shape errors before testing.</p>
            ) : from && from === to ? (
              <p>Choose two different locations.</p>
            ) : null}
            <p
              role="status"
              aria-live="polite"
              className="em-routing-route-test-status"
              data-status={status}
            >
              {status === "ready" && <Check size={14} aria-hidden="true" />}
              {message}
            </p>
          </>
        )}
      </div>
    </details>
  );
}
